import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useFormContext, Controller, useWatch } from 'react-hook-form';
import { TextField, IconButton, InputAdornment } from '@mui/material';
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import { get } from 'lodash';
import { cn } from '@src/utils/ui';

export const NumberInput = ({
    name,
    InputProps = {},
    withStepper = false,
    min = 0,
    max = Infinity,
    step = 1,
    ...rest
}) => {
    const { control, formState, setValue } = useFormContext();
    const value = useWatch({ control, name });
    const error = !!get(formState.errors, name);
    const helperText = get(formState.errors, `${name}.message`);

    const handleIncrement = useCallback(() => {
        const next = Math.min((Number(value) || 0) + step, max);
        setValue(name, next);
    }, [value, step, max, name, setValue]);

    const handleDecrement = useCallback(() => {
        const next = Math.max((Number(value) || 0) - step, min);
        setValue(name, next);
    }, [value, step, min, name, setValue]);

    return (
        <Controller
            name={name}
            control={control}
            render={({ field: { onChange, value, ...fieldRest } }) => (
                <TextField
                    {...fieldRest}
                    {...rest}
                    error={error}
                    helperText={helperText}
                    type='number'
                    InputProps={{
                        ...InputProps,
                        className: cn('tw-text-[16px]', InputProps.className),
                        endAdornment: withStepper && (
                            <InputAdornment position='end'>
                                <div className='tw-flex tw-flex-col tw-gap-[4px]'>
                                    <IconButton size='small' onClick={handleIncrement} sx={{ p: 0, lineHeight: 1 }}>
                                        <KeyboardArrowUp size={16} />
                                    </IconButton>
                                    <IconButton size='small' onClick={handleDecrement} sx={{ p: 0, lineHeight: 1 }}>
                                        <KeyboardArrowDown size={16} />
                                    </IconButton>
                                </div>
                            </InputAdornment>
                        )
                    }}
                    SelectProps={{ className: 'tw-text-[16px]' }}
                    InputLabelProps={{ className: 'tw-text-[16px]' }}
                    FormHelperTextProps={{ className: 'tw-text-[12px]' }}
                    value={value ?? ''}
                    onChange={e => {
                        const val = e.target.value;
                        onChange(val === '' ? '' : Math.max(min, Math.min(Number(val), max)));
                    }}
                />
            )}
        />
    );
};

NumberInput.propTypes = {
    name: PropTypes.string.isRequired,
    InputProps: PropTypes.object,
    withStepper: PropTypes.bool,
    min: PropTypes.number,
    max: PropTypes.number,
    step: PropTypes.number
};
